import { PropsWithSlots, styled } from '@cp/ds';
import { Box } from '@cp/ds/src/components/box';
import { Stack } from '@cp/ds/src/components/stack';
import { Typography } from '@cp/ds/src/components/typography';
import { withProps } from '@cp/ds/src/hocs';
import { Fragment, PropsWithChildren, ReactNode, useMemo } from 'react';
import { formatDate } from 'date-fns';
import { ru } from 'date-fns/locale';
import { Button } from '@cp/ds/src/components/button';
import { CareerId, careerDictionary } from '@common/const/catalog/career';
import { useTheme } from '@cp/ds/src/hooks';
import { CleanIcon, KidIcon, NurseIcon, TeacherIcon, VirusIcon } from '@cp/ds/src/icons';
import { SVGIconTokens } from '@cp/ds/src/components/svgIcon';

export type ClickType = 'from' | 'to' | 'order' | 'support';

interface Slots {
  foot?: ReactNode;
  to?: ReactNode;
  from?: ReactNode;
}

interface Labels {
  date: string;
}

interface Props extends PropsWithSlots<Slots> {
  labels?: Partial<Labels>;
  fake?: boolean;
  enableDim?: boolean;
  createdAt: Date;
  from: string;
  to: string;
  careerType: CareerId;
  changeTypeOfClients?: boolean;
  onClick: (type: ClickType) => void;
}

const defaultSlots: Slots = {};
const defaultLabels: Labels = {
  date: 'Дата',
};

const Wrap = withProps(
  styled(Stack)<{
    enableDim?: boolean;
  }>(({ theme, enableDim }) => ({
    padding: 16,
    borderRadius: 20,
    opacity: enableDim ? 0.6 : 1,
    backgroundColor: theme.palette.white,
    border: `1px solid ${theme.palette['gray light']}`,
  })),
  {
    spacing: 1,
    flexDirection: 'column',
  },
);

const MainRow = withProps(Stack, {
  gap: 4,
  justifyContent: 'space-between',
  flexDirection: 'row',
  alignItems: 'center',
  flexWrap: 'wrap',
  sx: {
    minHeight: 32,
  },
});

const DetailsRow = withProps(Stack, {
  gap: 4,
  flexDirection: 'row',
  alignItems: 'center',
  flexWrap: 'wrap',
});

const Divider = styled(Box)(({ theme }) => ({
  height: 1,
  width: '100%',
  backgroundColor: theme.palette['gray middle'],
}));

const WithDivider = ({ children }: PropsWithChildren) => {
  if (!children) return null;
  return (
    <>
      <Divider />
      {children}
    </>
  );
};

const WithValue = ({ label, value, children }: PropsWithChildren<{ label: string; value: ReactNode | string }>) => {
  return (
    <Stack alignItems="center" flexDirection="row" gap={1}>
      <Typography variant="caption">{label}:</Typography>
      <Typography variant="caption bold">{value}</Typography>
      {children}
    </Stack>
  );
};

const WithAction = ({ onClick, label }: { onClick: () => void; label: string }) => {
  const { palette } = useTheme();
  return (
    <Button onClick={onClick} variant="text" sx={{ color: palette['brand gray'], '&:hover': { color: palette['brand pink hover'] } }}>
      <Typography sx={{ textDecoration: 'underline dotted' }} variant="caption bold">
        {label}
      </Typography>
    </Button>
  );
};

const WithCareer = ({ careerType }: { careerType: CareerId }) => {
  const theme = useTheme();
  const Icon = useMemo(() => {
    switch (careerType) {
      case CareerId.Nanny:
        return KidIcon;
      case CareerId.Tutor:
        return TeacherIcon;
      case CareerId.Nurse:
        return NurseIcon;
      case CareerId.Housekeeper:
        return CleanIcon;
      default:
        return Fragment;
    }
  }, [careerType]);
  return (
    <Stack
      alignItems="center"
      flexDirection="row"
      gap={0.5}
      sx={{
        pr: 1,
        pl: 0.5,
        minHeight: 24,
        borderRadius: 1.5,
        color: theme.palette.gray,
        border: `1px solid ${theme.palette['gray light']}`,
        backgroundColor: theme.palette['gray ultra light'],
      }}
    >
      <Icon sx={{ [SVGIconTokens.Size]: '16px' }} />
      <Typography sx={{ pb: '1px' }} variant="caption bold">
        {careerDictionary[careerType].translation.nameSingle}
      </Typography>
    </Stack>
  );
};

export const DealModerationCardEntity = (props: Props) => {
  const slots = { ...defaultSlots, ...props.slots };
  const labels = { ...defaultLabels, ...props.labels };
  const hofClick = (type: ClickType) => () => props.onClick(type);
  const createdValue = useMemo(() => formatDate(props.createdAt, 'dd MMMM yyyy', { locale: ru }), [props.createdAt]);
  const [from, to] = useMemo(() => {
    const f = props.changeTypeOfClients ? 'От специалиста' : 'От нанимателя';
    const t = props.changeTypeOfClients ? 'Нанимателю' : 'Специалисту';
    return [f, t];
  }, [props.changeTypeOfClients]);
  return (
    <Wrap enableDim={props.enableDim}>
      <MainRow>
        <DetailsRow>
          {props.fake && <VirusIcon sx={{ mr: -2.5, color: '#5083C4' }} />}
          <WithValue label={from} value={<WithAction label={props.from} onClick={hofClick('from')} />}>
            {slots.from}
          </WithValue>
          <WithValue label={to} value={<WithAction label={props.to} onClick={hofClick('to')} />}>
            {slots.to}
          </WithValue>
          <WithValue label="Заказ" value={<WithCareer careerType={props.careerType} />}>
            <WithAction label="Детали заказа" onClick={hofClick('order')} />
          </WithValue>
          <WithAction label="ТП" onClick={hofClick('support')} />
        </DetailsRow>
        <WithValue label={labels.date} value={createdValue} />
      </MainRow>
      <WithDivider>{slots.foot}</WithDivider>
    </Wrap>
  );
};
